'use client';

import React, { useEffect, useState, useRef } from 'react';

// Global namespace for Turnstile
const globalNamespace = (typeof globalThis !== 'undefined' ? globalThis : window);
let turnstileState = typeof globalNamespace.turnstile !== 'undefined' ? 'ready' : 'unloaded';
let ensureTurnstile;

// Functions responsible for loading the turnstile api, while also making sure
// to only load it once
let turnstileLoad = {
  resolve: null,
  reject: null,
};

const turnstileLoadPromise = new Promise((resolve, reject) => {
  turnstileLoad.resolve = resolve;
  turnstileLoad.reject = reject;
  if (turnstileState === 'ready') resolve(undefined);
});

// Setup the Turnstile loading function
const TURNSTILE_LOAD_FUNCTION = 'cf__reactTurnstileOnLoad';
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

ensureTurnstile = () => {
  if (turnstileState === 'unloaded') {
    turnstileState = 'loading';
    globalNamespace[TURNSTILE_LOAD_FUNCTION] = () => {
      turnstileLoad.resolve();
      turnstileState = 'ready';
      delete globalNamespace[TURNSTILE_LOAD_FUNCTION];
    };
    const url = `${TURNSTILE_SRC}?onload=${TURNSTILE_LOAD_FUNCTION}&render=explicit`;
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.addEventListener('error', () => {
      turnstileLoad.reject('Failed to load Turnstile.');
      delete globalNamespace[TURNSTILE_LOAD_FUNCTION];
    });
    document.head.appendChild(script);
  }
  return turnstileLoadPromise;
};

const CustomTurnstile = ({
  id,
  className,
  style: customStyle,
  action,
  cData,
  theme = 'auto',
  language = 'auto',
  tabIndex,
  responseField = true,
  responseFieldName,
  size = 'normal',
  fixedSize = false,
  retry = 'auto',
  retryInterval,
  refreshExpired = 'auto',
  appearance = 'always',
  execution = 'render',
  userRef,
  onVerify,
  onSuccess,
  onLoad,
  onError,
  onExpire,
  onTimeout,
  onAfterInteractive,
  onBeforeInteractive,
  onUnsupported,
}) => {
  const ownRef = useRef(null);
  const [callbacks] = useState({
    onVerify,
    onSuccess,
    onLoad,
    onError,
    onExpire,
    onTimeout,
    onAfterInteractive,
    onBeforeInteractive,
    onUnsupported,
  });

  const ref = userRef ?? ownRef;

  const style = fixedSize
    ? {
      width: size === 'compact' ? '130px' : size === 'flexible' ? '100%' : '300px',
      height: size === 'compact' ? '120px' : '65px',
      ...customStyle,
    }
    : customStyle;

  useEffect(() => {
    if (!ref.current) return;

    let cancelled = false;
    let widgetId = '';
    let boundTurnstileObject;

    (async () => {
      // load turnstile
      if (turnstileState !== 'ready') {
        try {
          await ensureTurnstile();
        } catch (e) {
          callbacks.onError?.(e);
          return;
        }
      }

      if (cancelled || !ref.current) return;

      // Fetch the site key from the backend
      try {
        const response = await fetch('/api/turnstile/sitekey');
        const data = await response.json();

        if (!data.success || !data.sitekey) {
          callbacks.onError?.('Failed to get sitekey from backend');
          return;
        }

        const turnstileOptions = {
          sitekey: data.sitekey,
          action,
          cData,
          theme,
          language,
          tabindex: tabIndex,
          'response-field': responseField,
          'response-field-name': responseFieldName,
          size,
          retry,
          'retry-interval': retryInterval,
          'refresh-expired': refreshExpired,
          appearance,
          execution,
          callback: (token, preClearanceObtained) => {
            callbacks.onVerify?.(token, boundTurnstileObject);
            callbacks.onSuccess?.(token, preClearanceObtained, boundTurnstileObject);
          },
          'error-callback': (error) => callbacks.onError?.(error, boundTurnstileObject),
          'expired-callback': (token) => callbacks.onExpire?.(token, boundTurnstileObject),
          'timeout-callback': () => callbacks.onTimeout?.(boundTurnstileObject),
          'after-interactive-callback': () => callbacks.onAfterInteractive?.(boundTurnstileObject),
          'before-interactive-callback': () => callbacks.onBeforeInteractive?.(boundTurnstileObject),
          'unsupported-callback': () => callbacks.onUnsupported?.(boundTurnstileObject),
        };

        widgetId = window.turnstile.render(ref.current, turnstileOptions);

        boundTurnstileObject = {
          execute: (options) => window.turnstile.execute(widgetId, options),
          reset: () => window.turnstile.reset(widgetId),
          getResponse: () => window.turnstile.getResponse(widgetId),
          isExpired: () => window.turnstile.isExpired(widgetId),
        };

        callbacks.onLoad?.(widgetId, boundTurnstileObject);
      } catch (error) {
        callbacks.onError?.(error);
      }
    })();

    return () => {
      cancelled = true;
      if (widgetId) window.turnstile.remove(widgetId);
    };
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    action,
    cData,
    theme,
    language,
    tabIndex,
    responseField,
    responseFieldName,
    size,
    retry,
    retryInterval,
    refreshExpired,
    appearance,
    execution,
  ]);

  useEffect(() => {
    callbacks.onVerify = onVerify;
    callbacks.onSuccess = onSuccess;
    callbacks.onLoad = onLoad;
    callbacks.onError = onError;
    callbacks.onExpire = onExpire;
    callbacks.onTimeout = onTimeout;
    callbacks.onAfterInteractive = onAfterInteractive;
    callbacks.onBeforeInteractive = onBeforeInteractive;
    callbacks.onUnsupported = onUnsupported;
  },
  //eslint-disable-next-line react-hooks/exhaustive-deps
  [
    onVerify,
    onSuccess,
    onLoad,
    onError,
    onExpire,
    onTimeout,
    onAfterInteractive,
    onBeforeInteractive,
    onUnsupported,
  ]);

  return <div ref={ref} id={id} className={className} style={style} />;
};

export default CustomTurnstile;
